import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Drawer,
  Divider,
  Badge,
} from '@mui/material';
import { io } from 'socket.io-client';
import axios from '../api/axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import EventIcon from '@mui/icons-material/Event';
import DescriptionIcon from '@mui/icons-material/Description';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import SettingsIcon from '@mui/icons-material/Settings';
import ArticleIcon from '@mui/icons-material/Article';
import ScheduleIcon from '@mui/icons-material/Schedule';
import FolderIcon from '@mui/icons-material/Folder';
import SecurityIcon from '@mui/icons-material/Security';
import DynamicFormIcon from '@mui/icons-material/DynamicForm';
import BookmarksIcon from '@mui/icons-material/Bookmarks';
import RateReviewIcon from '@mui/icons-material/RateReview';

import SpecialistsManager from '../components/admin/SpecialistsManager';
import EventsManager from '../components/admin/EventsManager';
import RequestsManager from '../components/admin/RequestsManager';
import PagesManager from '../components/admin/PagesManager';
import SettingsManager from '../components/admin/SettingsManager';
import ConsultationsManager from '../components/admin/ConsultationsManager';
import ScheduleManager from '../components/admin/ScheduleManager';
import DocumentsManager from '../components/admin/DocumentsManager';
import PrivacyPolicyManager from '../components/admin/PrivacyPolicyManager';
import FormFieldsManager from '../components/admin/FormFieldsManager';
import UsefulResourcesManager from '../components/admin/UsefulResourcesManager';
import ReviewsManager from '../components/admin/ReviewsManager';

const drawerWidth = 280;

const menuItems = [
  { id: 0, icon: <SettingsIcon />, label: 'Настройки сайта', component: SettingsManager },
  { id: 1, icon: <ContactMailIcon />, label: 'Заявки', component: RequestsManager },
  { id: 2, icon: <DynamicFormIcon />, label: 'Поля формы', component: FormFieldsManager },
  { id: 3, icon: <PeopleIcon />, label: 'Специалисты', component: SpecialistsManager },
  { id: 4, icon: <EventIcon />, label: 'Мероприятия', component: EventsManager },
  { id: 5, icon: <ArticleIcon />, label: 'Консультации', component: ConsultationsManager },
  { id: 6, icon: <ScheduleIcon />, label: 'Расписание', component: ScheduleManager },
  { id: 7, icon: <FolderIcon />, label: 'Документы', component: DocumentsManager },
  { id: 8, icon: <BookmarksIcon />, label: 'Навигатор для родителей', component: UsefulResourcesManager },
  { id: 9, icon: <SecurityIcon />, label: 'Политика конфиденциальности', component: PrivacyPolicyManager },
  { id: 10, icon: <DescriptionIcon />, label: 'Страницы', component: PagesManager },
  { id: 11, icon: <RateReviewIcon />, label: 'Отзывы', component: ReviewsManager },
];

// Функция для правильного склонения
const getMessageText = (count) => {
  const lastDigit = count % 10;
  const lastTwoDigits = count % 100;
  
  if (lastTwoDigits >= 11 && lastTwoDigits <= 19) {
    return `${count} новых сообщений`;
  }
  
  if (lastDigit === 1) {
    return `${count} новое сообщение`;
  }
  
  if (lastDigit >= 2 && lastDigit <= 4) {
    return `${count} новых сообщения`;
  }
  
  return `${count} новых сообщений`;
};

function AdminPanel() {
  const [activeTab, setActiveTab] = useState(0);
  const [unviewedCount, setUnviewedCount] = useState(0);
  const navigate = useNavigate();
  const { logout } = useAuth();
  const socketRef = useRef(null);

  // Обновление заголовка вкладки
  const updateDocumentTitle = React.useCallback((count) => {
    if (count > 0) {
      document.title = `${getMessageText(count)}`;
    } else {
      document.title = 'Панель управления';
    }
  }, []);

  // Загрузка количества непросмотренных заявок
  const fetchUnviewedCount = React.useCallback(async () => {
    try {
      const response = await axios.get('/requests/unviewed-count');
      const count = response.data.count;
      setUnviewedCount(count);
    } catch (error) {
      console.error('Ошибка загрузки счетчика заявок:', error);
    }
  }, []);

  useEffect(() => {
    // Загружаем начальное количество
    fetchUnviewedCount();

    // Подключаемся к WebSocket
    // Определяем базовый URL для Socket.io
    let socketUrl;
    const apiUrl = process.env.REACT_APP_API_URL;
    
    if (!apiUrl || apiUrl === '/api' || apiUrl.startsWith('/api')) {
      // Относительный URL или не установлен - используем текущий origin
      socketUrl = window.location.origin;
    } else if (apiUrl.startsWith('http://') || apiUrl.startsWith('https://')) {
      // Абсолютный URL - убираем /api
      socketUrl = apiUrl.replace('/api', '');
    } else {
      // Fallback на текущий origin
      socketUrl = window.location.origin;
    }
    
    socketRef.current = io(socketUrl, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
    });

    // Слушаем события о новых заявках
    socketRef.current.on('new-request', (data) => {
      setUnviewedCount(data.count);
    });

    // Слушаем события об обновлении заявок
    socketRef.current.on('request-updated', (data) => {
      setUnviewedCount(data.count);
    });

    // Обработка ошибок подключения
    socketRef.current.on('connect_error', (error) => {
      console.error('Ошибка подключения WebSocket:', error);
    });

    return () => {
      if (socketRef.current) {
        console.log('Отключение WebSocket');
        socketRef.current.disconnect();
      }
      // Восстанавливаем заголовок при размонтировании
      document.title = 'Панель управления';
    };
  }, [fetchUnviewedCount]);

  // Обновляем заголовок при изменении счетчика
  useEffect(() => {
    updateDocumentTitle(unviewedCount);
  }, [unviewedCount, updateDocumentTitle]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleMenuClick = (index) => {
    setActiveTab(index);
    // Если открыли раздел "Заявки", обновляем счетчик
    if (index === 1) {
      fetchUnviewedCount();
    }
  };

  const ActiveComponent = menuItems[activeTab].component;

  // Обработка ошибок рендеринга компонентов
  const renderActiveComponent = () => {
    try {
      return <ActiveComponent />;
    } catch (error) {
      console.error('Ошибка рендеринга компонента:', error);
      return (
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" color="error">
            Ошибка загрузки компонента
          </Typography>
          <Typography variant="body2" sx={{ mt: 2 }}>
            {error.message}
          </Typography>
        </Box>
      );
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Верхняя панель */}
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'primary.main',
        }}
      >
        <Toolbar>
          <DashboardIcon sx={{ mr: 2 }} />
          <Typography variant="h6" noWrap sx={{ flexGrow: 1 }}>
            Панель управления
          </Typography>
          <Button color="inherit" onClick={() => navigate('/')}>
            На сайт
          </Button>
          <Button
            color="inherit"
            onClick={handleLogout}
            startIcon={<ExitToAppIcon />}
          >
            Выход
          </Button>
        </Toolbar>
      </AppBar>

      {/* Боковое меню */}
      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            backgroundColor: '#23282d',
            color: '#fff',
          },
        }}
      >
        <Toolbar /> {/* Отступ под AppBar */}
        <Box sx={{ overflow: 'auto', mt: 2 }}>
          <List>
            {menuItems.map((item) => (
              <ListItem key={item.id} disablePadding>
                <ListItemButton
                  selected={activeTab === item.id}
                  onClick={() => handleMenuClick(item.id)}
                  sx={{
                    py: 1.5,
                    px: 2,
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(30, 140, 190, 0.3)',
                      borderLeft: '4px solid #0073aa',
                      '&:hover': {
                        backgroundColor: 'rgba(30, 140, 190, 0.4)',
                      },
                    },
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    },
                  }}
                >
                  <ListItemIcon sx={{ color: '#a0a5aa', minWidth: 40 }}>
                    {item.id === 1 && unviewedCount > 0 ? (
                      <Badge
                        badgeContent={unviewedCount}
                        color="error"
                        sx={{
                          '& .MuiBadge-badge': {
                            fontSize: '11px',
                            minWidth: '18px',
                            height: '18px',
                            padding: '0 4px',
                            fontWeight: 600,
                          },
                        }}
                      >
                        {item.icon}
                      </Badge>
                    ) : (
                      item.icon
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontSize: '14px',
                      fontWeight: activeTab === item.id ? 600 : 400,
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>

      {/* Основной контент */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          backgroundColor: '#f1f1f1',
          p: 3,
          minHeight: '100vh',
        }}
      >
        <Toolbar /> {/* Отступ под AppBar */}
        <Box
          sx={{
            backgroundColor: '#fff',
            borderRadius: 1,
            p: 3,
            boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
            minHeight: 'calc(100vh - 100px)',
          }}
        >
          <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: '#23282d' }}>
            {menuItems[activeTab].label}
          </Typography>
          <Divider sx={{ mb: 3 }} />
          {activeTab === 1 ? (
            <RequestsManager onRequestViewed={fetchUnviewedCount} />
          ) : (
            renderActiveComponent()
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default AdminPanel;
